(function (root, factory) {
  "use strict";
  const exported = factory();
  if (typeof module === "object" && module.exports) module.exports = exported;
  else root.LouderrPostArchive = Object.freeze(exported);
})(globalThis, () => {
  "use strict";
  const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  function create(hooks) {
    const doc = hooks.document || document;
    const el = Object.fromEntries(["dialog", "title", "status", "error", "back", "refresh", "list", "more",
      "detail", "change", "delete", "confirmation", "confirm-text", "confirm", "keep", "retry"]
      .map(key => [key, doc.getElementById("post-archive-" + key)]));
    const owner = () => hooks.viewer()?.id || null, scope = () => hooks.scope();
    let identity = owner(), accountScope = scope(), epoch = 0, serial = 0, session = null;
    // Retain an uncertain operation across dialog closes. Never retry a write automatically.
    const attempts = new Map();
    const accountCurrent = s => s.owner === owner() && s.scope === scope() && s.epoch === epoch;
    const current = s => Boolean(s && session === s && accountCurrent(s) && hooks.canRead() && el.dialog.open);
    const opFor = s => attempts.get(s?.id);
    const usable = s => current(s) && s.ready && !s.loading && !opFor(s);
    const mutable = s => usable(s) && s.state && (s.state.status === "active" || s.state.can_restore && hooks.canRestore());
    const deletable = s => usable(s) && s.state?.can_restore;
    const allowed = op => hooks.canRead() && (op.kind === "delete" || op.archived || hooks.canRestore());
    const statePath = id => `/api/drops/${encodeURIComponent(id)}/archive`;
    const privatePath = id => `/api/me/archive/${encodeURIComponent(id)}`;
    const read = (url, options = {}) => hooks.api(url, {...options, headers:{"Cache-Control":"no-cache"}});
    const make = (tag, text = "", className = "") => {
      const node = doc.createElement(tag); node.textContent = text; node.className = className; return node;
    };
    function button(text, action) {
      const node = make("button", text, "post-archive-choice"); node.type = "button";
      node.addEventListener("click", action); return node;
    }
    function checkedState(value, id) {
      if (!value || value.drop_id !== id || !UUID.test(id) || !Number.isSafeInteger(value.revision)
          || value.revision < 0 || value.revision > 2147483647 || typeof value.archived !== "boolean"
          || typeof value.can_restore !== "boolean" || !["active", "archived", "removed"].includes(value.status)
          || value.can_restore !== (value.status === "archived" && value.archived)
          || value.last_operation_id != null && !UUID.test(value.last_operation_id)) {
        throw new Error("Post details could not be verified. Refresh to continue.");
      }
      return value;
    }
    function checkedPost(post, s, id) {
      if (!post || post.id !== id || post.author_id !== s.owner || typeof post.title !== "string"
          || typeof post.body !== "string" || !Array.isArray(post.media)) throw new Error("Post details could not be verified.");
      return post;
    }
    function checkedPrivate(value, s, id) {
      const state = checkedState(value?.archive, id);
      if (!state.can_restore) throw new Error("This post is no longer in your archive.");
      return {post:checkedPost(value.post, s, id), archive:state};
    }
    function release() { hooks.releaseMedia?.(el.detail); el.detail.replaceChildren(); }
    function clear(s) { release(); s.post = null; s.state = null; s.ready = false; s.confirmation = false; s.context = null; }
    function finishClose() {
      const old = session; session = null;
      release(); el.list.replaceChildren();
      for (const key of ["status", "error", "confirm-text"]) el[key].textContent = "";
      el.title.textContent = "Post archive";
      if (old && accountCurrent(old)) hooks.restoreFocus?.(old.anchor);
    }
    function close() { if (el.dialog.open) el.dialog.close(); if (session) finishClose(); }
    function syncIdentity() {
      if (identity === owner() && accountScope === scope() && hooks.canRead()) return false;
      identity = owner(); accountScope = scope(); ++epoch; attempts.clear(); close(); return true;
    }
    function paint(s) {
      if (!current(s)) return;
      const op = opFor(s);
      el.title.textContent = s.id ? s.post?.title || "Post archive" : "Post archive";
      el.status.textContent = s.loading ? "Loading…" : op?.busy ? "Saving…"
        : op ? "The change is not confirmed. Refresh to check it before retrying."
        : s.notice || (s.state?.status === "active" ? "This post is currently published."
          : "Only you can see posts in your archive. Posts are ordered by their original publication date.");
      el.error.textContent = s.error || ""; el.error.hidden = !s.error;
      el.back.hidden = !s.id; el.back.disabled = Boolean(op?.busy);
      el.refresh.disabled = s.loading || Boolean(op?.busy);
      el.more.hidden = Boolean(s.id) || !s.next; el.more.disabled = !usable(s);
      el.change.hidden = !s.id || !s.state || s.state.status === "removed";
      el.change.textContent = s.state?.can_restore ? "Restore post" : "Archive post";
      el.change.disabled = !mutable(s);
      el.delete.hidden = !s.state?.can_restore;
      el.delete.disabled = !deletable(s);
      el.confirmation.hidden = !s.confirmation;
      const deleting = s.confirmation === "delete";
      el["confirm-text"].textContent = !s.confirmation ? "" : deleting
        ? "Delete this post? It will be removed from your posts and archive. You cannot restore it from the archive."
        : s.state?.can_restore
        ? "Restore this post for its original audience? Its original date and interactions will be kept."
        : "Move this post to your private archive? It will disappear from public post views until you restore it.";
      el.confirm.textContent = deleting ? "Delete post" : s.state?.can_restore ? "Restore post" : "Move to archive";
      el.confirm.disabled = !(deleting ? deletable(s) : mutable(s)) || !s.confirmation;
      el.keep.disabled = Boolean(op?.busy);
      el.retry.hidden = !op || op.busy;
      el.retry.textContent = op?.kind === "delete" ? "Retry this deletion" : "Retry the same change";
      el.retry.disabled = s.loading || !op?.readback || !allowed(op);
    }
    function content(s) {
      el.list.replaceChildren(); release();
      if (!s.ready) return;
      if (s.id) {
        if (!s.post) return;
        el.detail.append(make("p", s.post.body, "post-archive-copy"));
        if (s.state.can_restore) {
          s.context = `archive:${++serial}`;
          const media = hooks.renderMedia?.(s.post, s.context);
          if (media) el.detail.append(media);
        }
        if (s.post.created_at) el.detail.append(make("p", `Published ${new Date(s.post.created_at).toLocaleString()}`, "metric-note"));
        return;
      }
      const shown = new Set();
      for (const item of s.items) {
        shown.add(item.post.id);
        const node = button(item.post.title || "Untitled post", () => choose(s, item.post.id));
        node.append(make("small", `Published ${new Date(item.post.created_at).toLocaleString()}`));
        el.list.append(node);
      }
      for (const [id, op] of attempts) if (!shown.has(id)) {
        el.list.append(button(`Check pending change: ${op.title}`, () => choose(s, id)));
      }
      if (!el.list.children.length) el.list.append(make("p", "No archived posts yet. Open the menu on one of your posts to archive it.", "metric-note"));
    }
    function reconcile(s, value) {
      const op = opFor(s);
      if (!op || op.busy) return;
      if (op.kind === "delete") {
        if (value.can_restore && value.revision === op.expected_revision) op.readback = true;
        else { attempts.delete(s.id); s.error = "This post changed. Review its current state before confirming deletion again."; }
        return;
      }
      if (value.last_operation_id === op.operation_id && value.revision === op.expected_revision + 1 && value.archived === op.archived) {
        attempts.delete(s.id); s.notice = op.archived ? "Post archived." : "Post restored.";
        hooks.changed?.(s.id, op.archived);
      } else if (value.revision !== op.expected_revision || value.status !== (op.archived ? "active" : "archived")) {
        attempts.delete(s.id); s.error = "This post changed. Review its current state before making another change.";
      } else op.readback = true;
    }
    async function load(s, append = false, focus = false) {
      if (!current(s) || opFor(s)?.busy) return;
      const ticket = ++s.read, live = () => current(s) && s.read === ticket;
      const cursor = append ? s.next : null;
      s.loading = true; s.error = "";
      if (!append) { clear(s); s.items = []; s.next = null; s.cursors.clear(); }
      if (opFor(s)) opFor(s).readback = false;
      paint(s); if (!append) el.list.replaceChildren();
      try {
        if (s.id) {
          let value = checkedState(await read(statePath(s.id)), s.id);
          if (!live()) return;
          let post = null;
          if (value.can_restore) {
            const result = checkedPrivate(await read(privatePath(s.id)), s, s.id);
            value = result.archive; post = result.post;
          } else if (value.status === "active") post = checkedPost(await read(`/api/drops/${encodeURIComponent(s.id)}`), s, s.id);
          if (!live()) return;
          // A later owner/moderation change can supersede a local success notice.
          if (s.notice === "Post archived." && !value.can_restore
              || s.notice === "Post restored." && value.status !== "active") s.notice = "";
          s.state = value; s.post = post; reconcile(s, value);
          if (value.status === "removed") s.error = "This post is unavailable. Moderation removal cannot be undone from the archive.";
          else if (value.can_restore && !hooks.canRestore()) s.notice = "Restoration is temporarily unavailable. Your post remains in the archive.";
        } else {
          const data = await read("/api/me/archive?limit=20" + (cursor ? `&cursor=${encodeURIComponent(cursor)}` : ""));
          if (!live()) return;
          if (!Array.isArray(data?.items) || data.items.length > 20 || data.next_cursor != null
              && (typeof data.next_cursor !== "string" || !data.next_cursor || !data.items.length || s.cursors.has(data.next_cursor))) {
            throw new Error("Archive page could not be verified. Refresh to continue.");
          }
          const rows = data.items.map(row => checkedPrivate(row, s, row?.post?.id));
          if (new Set(rows.map(row => row.post.id)).size !== rows.length) throw new Error("Archive page could not be verified.");
          const known = new Set(s.items.map(row => row.post.id));
          s.items.push(...rows.filter(row => !known.has(row.post.id)));
          s.next = data.next_cursor || null;
          if (s.next) s.cursors.add(s.next);
        }
        s.unconfirmedDeletion = null; s.ready = true; content(s);
      } catch (error) {
        if (!live()) return;
        clear(s); s.items = []; s.next = null; el.list.replaceChildren();
        if ([401, 403, 404].includes(error.status)) {
          const uncertainDeletion = error.status === 404
            && (opFor(s)?.kind === "delete" || s.unconfirmedDeletion === s.id);
          attempts.delete(s.id); s.error = "This post or archive is unavailable. Refresh after checking your account.";
          if (uncertainDeletion) {
            s.unconfirmedDeletion = s.id;
            s.error = "This post is no longer available. The deletion result could not be confirmed.";
          }
        } else s.error = error.message || "The archive could not be checked. Refresh to try again.";
      } finally {
        if (live()) {
          s.loading = false; paint(s);
          if (focus || append && !s.next) el.title.focus();
          if (s.invalidated) { s.invalidated = false; void load(s); }
        }
      }
    }
    function choose(s, id) {
      if (!usable(s) || !UUID.test(id)) return;
      s.id = id; s.notice = ""; void load(s, false, true);
    }
    function show(drop = null, anchor = null) {
      syncIdentity(); close();
      if (!hooks.canRead()) { hooks.signIn?.(); return; }
      if (drop && (!UUID.test(drop.id) || drop.author_id !== owner() || drop.structured_type === "sponsor")) return;
      const s = {owner:owner(), scope:scope(), epoch, anchor, id:drop?.id || null, read:0,
        items:[], cursors:new Set(), next:null, ready:false, loading:false, notice:"", error:"", context:null};
      session = s; hooks.openDialog(el.dialog); void load(s, false, true);
    }
    async function send(s, op) {
      if (!current(s) || op.busy || !allowed(op)) return;
      const id = s.id;
      op.busy = true; op.readback = false; s.confirmation = false; s.notice = ""; s.error = ""; paint(s);
      try {
        if (op.kind === "delete") {
          const result = await hooks.api(`/api/drops/${encodeURIComponent(id)}?expected_archive_revision=${op.expected_revision}`, {method:"DELETE", csrf:true});
          if (!accountCurrent(s)) return;
          if (result?.deleted !== true || result.drop_id !== id) throw new Error("The deletion could not be confirmed.");
          attempts.delete(id); hooks.changed?.(id, true);
          if (current(session) && session.id === id) { clear(session); session.id = null; session.notice = "Post deleted."; }
          return;
        }
        const result = checkedState(await hooks.api(statePath(id), {method:"PATCH", csrf:true, json:{
          archived:op.archived, expected_revision:op.expected_revision, operation_id:op.operation_id,
        }}), id);
        if (!accountCurrent(s)) return;
        if (result.last_operation_id !== op.operation_id || result.revision !== op.expected_revision + 1 || result.archived !== op.archived) {
          throw new Error("The change could not be confirmed.");
        }
        attempts.delete(id); hooks.changed?.(id, op.archived);
        if (session?.id === id && current(session)) session.notice = op.archived ? "Post archived." : "Post restored.";
      } catch (error) {
        if (!accountCurrent(s)) return;
        if ([400, 401, 403, 404, 409, 422].includes(error.status)) attempts.delete(id);
        if (session?.id === id && current(session)) session.notice = error.status === 409
          ? "The post changed or cannot be restored. Review its current state before confirming again."
          : "The change was not confirmed. Its current state will be checked.";
      } finally {
        op.busy = false;
        if (accountCurrent(s) && current(session) && (!session.id || session.id === id)) void load(session);
      }
    }
    async function readPost(id, signal, context) {
      const s = session, ticket = s?.read;
      const valid = () => current(s) && s.id === id && s.context === context && s.read === ticket && s.ready && !s.loading;
      if (!context || !valid()) throw new Error("Archive view closed or changed.");
      try {
        const value = checkedPrivate(await read(privatePath(id), {signal}), s, id);
        if (!valid()) throw new Error("Archive view closed or changed.");
        return value.post;
      } catch (error) {
        if (valid() && [401, 403, 404].includes(error.status)) {
          clear(s); s.error = "This archived post is no longer available. Refresh to check its state."; paint(s);
        }
        throw error;
      }
    }
    function event(id = null) {
      const s = session;
      if (!current(s) || id && s.id && id !== s.id) return;
      s.confirmation = false;
      if (s.loading || opFor(s)?.busy) { s.invalidated = true; return; }
      void load(s);
    }
    el.dialog.addEventListener("close", finishClose);
    el.refresh.addEventListener("click", () => { if (session) void load(session); });
    el.more.addEventListener("click", () => { if (usable(session) && session.next) void load(session, true); });
    el.back.addEventListener("click", () => {
      if (!current(session) || opFor(session)?.busy) return;
      session.id = null; session.notice = ""; void load(session, false, true);
    });
    el.change.addEventListener("click", () => {
      if (!mutable(session)) return;
      session.confirmation = true; paint(session); el.confirm.focus();
    });
    el.delete.addEventListener("click", () => {
      if (!deletable(session)) return;
      session.confirmation = "delete"; paint(session); el.confirm.focus();
    });
    el.keep.addEventListener("click", () => {
      if (current(session)) {
        const target = session.confirmation === "delete" ? el.delete : el.change;
        session.confirmation = false; paint(session); target.focus();
      }
    });
    el.confirm.addEventListener("click", () => {
      const s = session;
      if (s?.confirmation === "delete") {
        if (!deletable(s)) return;
        const op = {kind:"delete", expected_revision:s.state.revision, title:s.post?.title || "Post"};
        attempts.set(s.id, op); void send(s, op); return;
      }
      if (!mutable(s) || !s.confirmation) return;
      const operation_id = hooks.uuid();
      if (!UUID.test(operation_id)) { s.error = "A change identifier could not be created. Refresh to try again."; paint(s); return; }
      const op = {operation_id, expected_revision:s.state.revision, archived:s.state.status === "active", title:s.post?.title || "Post"};
      attempts.set(s.id, op); void send(s, op);
    });
    el.retry.addEventListener("click", () => {
      const s = session, op = opFor(s);
      if (current(s) && !s.loading && op?.readback) void send(s, op);
    });
    return {show, close, syncIdentity, readPost, event};
  }
  return {create};
});
